import { ref, computed, watch, onBeforeUnmount } from 'vue'

/**
 * Composable for detecting text overflow and conditionally showing tooltips.
 *
 * Automatically observes the element for size changes via ResizeObserver —
 * consumers do NOT need mounted/updated hooks.
 *
 * @param {Object} options - Configuration options
 * @param {Function|String} options.tooltipContent - Function that returns tooltip content or static string
 * @param {Object} options.overflowStyles - Custom styles for overflow behavior (optional)
 * @returns {Object} - Composable return object
 *
 * @example
 * const { elementRef, tooltipContent, overflowStyles } = useOverflowTooltip({
 *   tooltipContent: () => someComputedValue.value
 * })
 * // In template:
 * <div ref="elementRef" v-tooltip="tooltipContent" :style="overflowStyles">...</div>
 */
export function useOverflowTooltip(options = {}) {
  const elementRef = ref(null)
  const isOverflowing = ref(false)
  let observer = null

  const defaultOverflowStyles = {
    'text-overflow': 'ellipsis',
    overflow: 'hidden',
  }

  const overflowStyles = computed(() => ({
    ...defaultOverflowStyles,
    ...options.overflowStyles,
  }))

  const checkOverflow = () => {
    if (elementRef.value) {
      const el = elementRef.value
      isOverflowing.value = el.scrollWidth > el.clientWidth
    }
  }

  // Watch the template ref: when the element mounts (or changes),
  // start observing; when it unmounts, disconnect.
  watch(
    elementRef,
    (el, _oldEl, onCleanup) => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (el) {
        checkOverflow()
        observer = new ResizeObserver(checkOverflow)
        observer.observe(el)
        onCleanup(() => {
          observer?.disconnect()
          observer = null
        })
      }
    },
    { flush: 'post' },
  )

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
  })

  const tooltipContent = computed(() => {
    if (isOverflowing.value) {
      if (typeof options.tooltipContent === 'function') {
        return options.tooltipContent()
      }
      return options.tooltipContent
    }
    return undefined
  })

  return {
    elementRef,
    isOverflowing,
    overflowStyles,
    checkOverflow,
    tooltipContent,
  }
}
